import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import NewBadge from '../components/NewBadge';
import { BookOpen, Plus, Trash, CheckCircle, XCircle, Spinner, Pencil, PaperPlaneTilt, X } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

const blankQuestion = (type = 'mc') => type === 'mc'
  ? { type: 'mc', prompt: '', options: ['', '', ''], correct_index: 0 }
  : { type: 'short', prompt: '', expected_keywords: [] };

/**
 * ReadingKeywordsField — controlled keyword tag input with a LOCAL raw
 * string state. The previous version parsed the entire input on every
 * keystroke, so typing `;` (the separator) or backspacing to fix a
 * misspelling was impossible — the parsed keyword would immediately
 * "swallow" the character and re-render as a trimmed value. Now we keep
 * whatever the teacher is currently typing verbatim and only commit
 * chips when they blur, press Enter, or type the separator explicitly.
 */
function ReadingKeywordsField({ keywords, onChange, testIdPrefix }) {
  const [raw, setRaw] = React.useState('');

  const commit = React.useCallback((value) => {
    const parts = value.split(';').map(s => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    // Merge with existing, dedup case-insensitively.
    const merged = [...keywords];
    for (const p of parts) {
      if (!merged.some(k => k.toLowerCase() === p.toLowerCase())) merged.push(p);
    }
    onChange(merged);
    setRaw('');
  }, [keywords, onChange]);

  const onKeyDown = (e) => {
    // Enter always commits. `;` as the last char in the buffer commits and
    // clears — the teacher can chain "viaje;mochila;playa" naturally.
    if (e.key === 'Enter' || e.key === ';') {
      e.preventDefault();
      commit(raw);
    } else if (e.key === 'Backspace' && !raw && keywords.length) {
      // Empty buffer + Backspace: remove the last chip (Twitter-style tag input).
      e.preventDefault();
      onChange(keywords.slice(0, -1));
    }
  };

  return (
    <div className="space-y-1.5">
      <input
        className="w-full border border-stone-300 rounded px-2 py-1 text-sm"
        placeholder="Escribe una palabra clave y pulsa ; o Enter"
        value={raw}
        onChange={e => setRaw(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => raw.trim() && commit(raw)}
        data-testid={`${testIdPrefix}-input`}
      />
      {keywords.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {keywords.map((k, ki) => (
            <span key={ki} className="inline-flex items-center gap-1 text-[11px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold" data-testid={`${testIdPrefix}-${ki}`}>
              {k}
              <button
                type="button"
                onClick={() => onChange(keywords.filter((_, kj) => kj !== ki))}
                className="text-emerald-600 hover:text-emerald-900"
                aria-label={`Remove ${k}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <p className="text-[11px] text-[#78716C]">
        Escribe una palabra y pulsa <strong>;</strong> o <strong>Enter</strong> para añadirla como chip. Backspace en el campo vacío borra la última.
      </p>
    </div>
  );
}

export default function ReadingPage() {
  const { user, demoMode } = useAuth();
  const { lang } = useLang();
  const isDe = lang === 'de';
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ title: '', text: '', level: 'A2', questions: [] });
  const [openTaskId, setOpenTaskId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState({}); // taskId → {score,total,results[]}
  const [submitting, setSubmitting] = useState(false);
  // Teachers can flip this on to interact with the task exactly like a
  // student does — radios are clickable, submit hits the same /submit
  // endpoint, AI feedback runs. Defaults to the global demoMode so when the
  // teacher has activated platform-wide demo mode every Lesen opens already
  // in student view; can still be toggled per page.
  const [studentMode, setStudentMode] = useState(demoMode);
  useEffect(() => { setStudentMode(demoMode); }, [demoMode]);

  // Teacher-only: edit + resend state. Loaded lazily.
  const [editingTask, setEditingTask] = useState(null);
  const [resendState, setResendState] = useState(null); // { task, selected: Set }
  const [groups, setGroups] = useState([]);
  useEffect(() => {
    if (!isTeacher) return;
    axios.get(`${API}/groups`, { withCredentials: true })
      .then(r => setGroups(r.data || []))
      .catch(() => setGroups([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reading-tasks`, { withCredentials: true });
      setTasks(res.data || []);
    } catch { toast.error('Error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetDraft = () => {
    setCreating(false);
    setDraft({ title: '', text: '', level: 'A2', questions: [] });
  };

  const addQuestion = (type) => setDraft(p => ({ ...p, questions: [...p.questions, blankQuestion(type)] }));
  const updateQuestion = (idx, patch) => setDraft(p => {
    const qs = [...p.questions]; qs[idx] = { ...qs[idx], ...patch }; return { ...p, questions: qs };
  });
  const removeQuestion = (idx) => setDraft(p => ({ ...p, questions: p.questions.filter((_, i) => i !== idx) }));

  const saveTask = async () => {
    if (!draft.title.trim() || draft.text.trim().length < 30) return toast.error('Título y texto (≥30 caracteres)');
    if (!draft.questions.length) return toast.error('Añade al menos una pregunta');
    try {
      await axios.post(`${API}/reading-tasks`, draft, { withCredentials: true });
      toast.success('Ejercicio creado');
      resetDraft(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteTask = async (id) => {
    if (!window.confirm('¿Borrar ejercicio?')) return;
    try { await axios.delete(`${API}/reading-tasks/${id}`, { withCredentials: true }); load(); }
    catch { toast.error('Error'); }
  };

  const submit = async (task) => {
    setSubmitting(true);
    try {
      const body = { answers: task.questions.map((_, i) => answers[`${task.task_id}-${i}`] ?? null) };
      const res = await axios.post(`${API}/reading-tasks/${task.task_id}/submit`, body, { withCredentials: true });
      setResults(p => ({ ...p, [task.task_id]: res.data }));
      toast.success(`${res.data.score}/${res.data.total} correctas`);
    } catch { toast.error('Error al enviar'); }
    finally { setSubmitting(false); }
  };

  const saveEditingTask = async () => {
    if (!editingTask) return;
    if (!editingTask.title?.trim() || (editingTask.text || '').trim().length < 30) return toast.error('Título y texto (≥30 caracteres)');
    try {
      await axios.patch(`${API}/reading-tasks/${editingTask.task_id}`, {
        title: editingTask.title,
        text: editingTask.text,
        level: editingTask.level,
        questions: editingTask.questions,
        group_id: editingTask.group_id || '',
      }, { withCredentials: true });
      toast.success('Ejercicio actualizado');
      setEditingTask(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openResend = (task) => setResendState({ task, selected: new Set() });
  const toggleResendTarget = (gid) => setResendState(s => {
    if (!s) return s;
    const next = new Set(s.selected);
    if (next.has(gid)) next.delete(gid); else next.add(gid);
    return { ...s, selected: next };
  });
  const submitResend = async () => {
    if (!resendState) return;
    const targets = Array.from(resendState.selected);
    if (targets.length === 0) return toast.error('Selecciona al menos una clase');
    try {
      await axios.post(`${API}/reading-tasks/${resendState.task.task_id}/resend`,
        { group_ids: targets }, { withCredentials: true });
      toast.success(`Enviado a ${targets.length} clase${targets.length === 1 ? '' : 's'}`);
      setResendState(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // When a student opens a task, re-hydrate their previous submission so the
  // answers + AI feedback stay visible instead of vanishing on refresh.
  // Teacher / admin don't hit the endpoint (backend returns null anyway).
  useEffect(() => {
    if (!openTaskId || isTeacher) return;
    if (results[openTaskId]) return; // already loaded (either from submit() or a prior open)
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/reading-tasks/${openTaskId}/my-submission`, { withCredentials: true });
        const sub = res.data?.submission;
        if (!sub || cancelled) return;
        setResults(p => ({ ...p, [openTaskId]: { score: sub.score, total: sub.total, results: sub.results } }));
        // Also restore answers so radios stay selected / textareas keep the text.
        setAnswers(p => {
          const next = { ...p };
          (sub.answers || []).forEach((a, i) => { next[`${openTaskId}-${i}`] = a; });
          return next;
        });
      } catch { /* silent — endpoint returns null when no submission */ }
    })();
    return () => { cancelled = true; };
  }, [openTaskId, isTeacher, results]);

  return (
    <div className="space-y-5" data-testid="reading-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
            <BookOpen size={28} weight="duotone" className="text-[#2F6B50]" /> {isDe ? 'Lesen' : 'Leer'}
          </h1>
          <p className="text-sm text-[#57534E] mt-1">
            {isTeacher
              ? (isDe ? 'Erstelle Texte mit Fragen. Die KI korrigiert die Antworten automatisch.' : 'Crea textos con preguntas. La IA corrige automáticamente las respuestas del alumno.')
              : (isDe ? 'Lies den Text und beantworte die Fragen — die Korrektur ist sofort.' : 'Lee el texto y responde. Tu respuesta se corrige automáticamente.')}
          </p>
        </div>
        {isTeacher && !creating && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { setStudentMode(s => !s); setAnswers({}); setResults({}); }}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                studentMode
                  ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                  : 'bg-white text-[#292524] border-stone-300 hover:bg-stone-50'
              }`}
              title={isDe ? 'Schaltet zwischen Lehrer-Vorschau und echter Schüleransicht um' : 'Cambia entre la vista del profesor y la del alumno'}
              data-testid="reading-student-mode-toggle"
            >
              <span className={`inline-block w-2 h-2 rounded-full ${studentMode ? 'bg-white' : 'bg-emerald-500'}`} />
              {studentMode
                ? (isDe ? 'Schüleransicht aktiv' : 'Vista de alumno activa')
                : (isDe ? 'Als Schüler ansehen' : 'Ver como alumno')}
            </button>
            <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-[#AB1C24] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#8A151C]" data-testid="reading-new">
              <Plus size={16} /> Nuevo ejercicio
            </button>
          </div>
        )}
      </div>

      {isTeacher && creating && (
        <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-3" data-testid="reading-create-form">
          <input className="w-full border border-stone-300 rounded px-3 py-2 text-sm font-semibold" placeholder="Título (p.ej. El viaje de María)" value={draft.title} onChange={e => setDraft(p => ({ ...p, title: e.target.value }))} data-testid="reading-title" />
          <textarea className="w-full border border-stone-300 rounded px-3 py-2 text-sm" rows={7} placeholder="Pega aquí el texto en español…" value={draft.text} onChange={e => setDraft(p => ({ ...p, text: e.target.value }))} data-testid="reading-text" />
          <div className="flex gap-3 flex-wrap items-center">
            <label className="text-xs font-semibold text-[#57534E]">Nivel:</label>
            <select value={draft.level} onChange={e => setDraft(p => ({ ...p, level: e.target.value }))} className="border border-stone-300 rounded px-2 py-1 text-sm" data-testid="reading-level">
              {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-[#57534E]">Preguntas:</p>
            {draft.questions.map((q, i) => (
              <div key={i} className="bg-[#FAF9F6] border border-stone-200 rounded p-3 space-y-2">
                <div className="flex gap-2 items-center">
                  <span className="text-xs bg-stone-200 px-2 py-0.5 rounded">{q.type === 'mc' ? 'Opción múltiple' : 'Respuesta corta'}</span>
                  <button onClick={() => removeQuestion(i)} className="ml-auto text-red-600 text-xs"><Trash size={14} /></button>
                </div>
                <input className="w-full border border-stone-300 rounded px-2 py-1 text-sm" placeholder="Enunciado de la pregunta" value={q.prompt} onChange={e => updateQuestion(i, { prompt: e.target.value })} />
                {q.type === 'mc' ? (
                  <div className="space-y-1">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex gap-2 items-center">
                        <input type="radio" name={`corr-${i}`} checked={q.correct_index === oi} onChange={() => updateQuestion(i, { correct_index: oi })} />
                        <input className="flex-1 border border-stone-300 rounded px-2 py-1 text-sm" placeholder={`Opción ${oi + 1}`} value={opt} onChange={e => { const opts = [...q.options]; opts[oi] = e.target.value; updateQuestion(i, { options: opts }); }} />
                        {q.options.length > 2 && <button onClick={() => updateQuestion(i, { options: q.options.filter((_, k) => k !== oi) })} className="text-red-500 text-xs">×</button>}
                      </div>
                    ))}
                    {q.options.length < 6 && <button onClick={() => updateQuestion(i, { options: [...q.options, ''] })} className="text-xs text-[#AB1C24]">+ Opción</button>}
                  </div>
                ) : (
                  <ReadingKeywordsField
                    keywords={q.expected_keywords || []}
                    onChange={(next) => updateQuestion(i, { expected_keywords: next })}
                    testIdPrefix={`reading-new-kw-${i}`}
                  />
                )}
              </div>
            ))}
            <div className="flex gap-2">
              <button onClick={() => addQuestion('mc')} className="text-xs bg-stone-100 px-3 py-1.5 rounded hover:bg-stone-200">+ Opción múltiple</button>
              <button onClick={() => addQuestion('short')} className="text-xs bg-stone-100 px-3 py-1.5 rounded hover:bg-stone-200">+ Respuesta corta</button>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={saveTask} className="bg-[#AB1C24] text-white px-4 py-2 rounded font-semibold hover:bg-[#8A151C]" data-testid="reading-save">Publicar</button>
            <button onClick={resetDraft} className="bg-stone-200 px-4 py-2 rounded">Cancelar</button>
          </div>
        </div>
      )}

      {loading ? <p className="text-center text-[#78716C] py-6">Cargando…</p>
        : tasks.length === 0 ? <p className="text-center text-[#78716C] py-10 bg-white rounded-xl border border-stone-200">Aún no hay ejercicios de lectura.</p>
        : tasks.map(t => {
          const open = openTaskId === t.task_id;
          const r = results[t.task_id];
          return (
            <div key={t.task_id} className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid={`reading-task-${t.task_id}`}>
              <header className="px-4 py-3 border-b border-stone-100 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-bold text-[#292524] truncate flex items-center gap-1.5">
                    <span className="truncate">{t.title}</span>
                    <NewBadge createdAt={t.created_at} authorRole="teacher" />
                  </h3>
                  <p className="text-xs text-[#78716C]">{t.level} · {t.questions.length} preguntas</p>
                </div>
                <div className="flex gap-2 items-center flex-shrink-0">
                  {isTeacher && (
                    <>
                      <button
                        onClick={() => setEditingTask(t)}
                        className="text-amber-600 hover:text-amber-800 p-1.5"
                        data-testid={`reading-edit-${t.task_id}`}
                        title="Bearbeiten"
                      >
                        <Pencil size={14} weight="bold" />
                      </button>
                      <button
                        onClick={() => openResend(t)}
                        className="text-violet-600 hover:text-violet-800 p-1.5"
                        data-testid={`reading-resend-${t.task_id}`}
                        title="An andere Klassen zuweisen"
                      >
                        <PaperPlaneTilt size={14} weight="duotone" />
                      </button>
                      <button onClick={() => deleteTask(t.task_id)} className="text-red-500 hover:text-red-700 p-1.5" data-testid={`reading-del-${t.task_id}`}><Trash size={14} /></button>
                    </>
                  )}
                  <button onClick={() => setOpenTaskId(open ? null : t.task_id)} className="text-sm text-[#AB1C24] font-semibold">
                    {open ? 'Cerrar' : 'Abrir'}
                  </button>
                </div>
              </header>
              {open && (
                <div className="p-4 space-y-4">
                  <div className="bg-[#FAF9F6] rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap text-[#292524]">{t.text}</div>
                  {/* Student view: shown to actual students AND to teachers
                      who flipped on "Ver como alumno" (studentMode). The
                      teacher-preview block below only renders when the
                      teacher is in default (review) mode. */}
                  {(!isTeacher || studentMode) && (
                    <div className="space-y-3">
                      {t.questions.map((q, i) => {
                        const key = `${t.task_id}-${i}`;
                        const fb = r?.results?.[i];
                        return (
                          <div key={i} className={`rounded-lg p-3 border ${fb ? (fb.correct ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200') : 'bg-white border-stone-200'}`}>
                            <p className="text-sm font-semibold text-[#292524] mb-2">{i + 1}. {q.prompt}</p>
                            {q.type === 'mc' ? (
                              <div className="space-y-1">
                                {q.options.map((opt, oi) => (
                                  <label key={oi} className="flex items-center gap-2 text-sm">
                                    <input type="radio" name={key} checked={answers[key] === oi} onChange={() => setAnswers(p => ({ ...p, [key]: oi }))} disabled={!!fb} />
                                    {opt}
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <textarea className="w-full border border-stone-300 rounded px-2 py-1 text-sm" rows={2} value={answers[key] || ''} onChange={e => setAnswers(p => ({ ...p, [key]: e.target.value }))} disabled={!!fb} placeholder="Escribe tu respuesta…" />
                            )}
                            {fb && (
                              <p className={`text-xs mt-1 flex items-center gap-1 ${fb.correct ? 'text-emerald-700' : 'text-red-700'}`}>
                                {fb.correct ? <><CheckCircle size={14} weight="fill" /> Correcto</> : <><XCircle size={14} weight="fill" /> Incorrecto</>}
                                {fb.feedback && <span className="ml-2 italic">— {fb.feedback}</span>}
                              </p>
                            )}
                          </div>
                        );
                      })}
                      {!r && (
                        <button onClick={() => submit(t)} disabled={submitting} className="bg-[#AB1C24] text-white px-4 py-2 rounded font-semibold hover:bg-[#8A151C] disabled:opacity-60" data-testid={`reading-submit-${t.task_id}`}>
                          {submitting ? <Spinner size={16} className="animate-spin inline mr-2" /> : null}
                          Enviar respuestas
                        </button>
                      )}
                      {r && (
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <p className="text-sm font-bold text-[#292524]">Puntuación: {r.score}/{r.total}</p>
                          {isTeacher && studentMode && (
                            <button
                              onClick={() => { setResults(p => { const c = { ...p }; delete c[t.task_id]; return c; }); setAnswers(p => { const c = { ...p }; Object.keys(c).forEach(k => k.startsWith(`${t.task_id}-`) && delete c[k]); return c; }); }}
                              className="text-xs text-[#AB1C24] font-semibold underline"
                              data-testid={`reading-retry-${t.task_id}`}
                            >
                              {isDe ? 'Erneut probieren' : 'Probar otra vez'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {isTeacher && !studentMode && (
                    // Teacher gets a read-only preview of EXACTLY what the
                    // student sees — questions + options rendered as radios —
                    // with the correct answer pre-highlighted in green and a
                    // small "Lösung" badge. This makes it obvious at a glance
                    // that students DO see the answer options (a recurring
                    // confusion when the teacher logs in to verify).
                    <div className="space-y-3" data-testid="reading-teacher-preview">
                      <p className="text-[11px] uppercase tracking-wider font-bold text-[#78716C] flex items-center gap-1.5">
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                        {isDe ? 'Vorschau (so sehen die Schüler die Aufgabe)' : 'Vista previa (lo que verán los alumnos)'}
                      </p>
                      {t.questions.map((q, i) => (
                        <div key={i} className="rounded-lg p-3 border bg-white border-stone-200">
                          <p className="text-sm font-semibold text-[#292524] mb-2">{i + 1}. {q.prompt}</p>
                          {q.type === 'mc' ? (
                            <div className="space-y-1">
                              {(q.options || []).map((opt, oi) => {
                                const isAnswer = q.correct_index === oi;
                                return (
                                  <label
                                    key={oi}
                                    className={`flex items-center gap-2 text-sm rounded px-2 py-1 ${
                                      isAnswer ? 'bg-emerald-50 border border-emerald-200 font-medium text-emerald-900' : 'text-[#292524]'
                                    }`}
                                  >
                                    <input type="radio" disabled checked={isAnswer} readOnly />
                                    <span className="flex-1">{opt}</span>
                                    {isAnswer && (
                                      <span className="text-[10px] uppercase font-bold tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded">
                                        {isDe ? 'Lösung' : 'Solución'}
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            <div>
                              <textarea
                                className="w-full border border-stone-300 rounded px-2 py-1 text-sm bg-stone-50 italic text-stone-400"
                                rows={2}
                                disabled
                                placeholder={isDe ? 'Der Schüler schreibt hier seine Antwort…' : 'El alumno escribe aquí su respuesta…'}
                              />
                              {(q.expected_keywords || []).length > 0 && (
                                <p className="text-[11px] mt-1 text-emerald-700 flex items-center gap-1.5 flex-wrap">
                                  <span className="font-bold uppercase tracking-wider">{isDe ? 'Erwartet' : 'Esperado'}:</span>
                                  {(q.expected_keywords || []).map((k, ki) => (
                                    <span key={ki} className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">{k}</span>
                                  ))}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

      {/* Edit modal — teacher-only. Uses the same ReadingKeywordsField as
          the creation form so ; typing + Enter + backspace-to-remove all
          behave consistently. */}
      {editingTask && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingTask(null)} data-testid="reading-edit-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg text-[#292524] flex items-center gap-2">
                <Pencil size={18} weight="duotone" className="text-[#AB1C24]" /> Editar Lesen-Aufgabe
              </h3>
              <button onClick={() => setEditingTask(null)} className="text-stone-400 hover:text-stone-600"><X size={18} weight="bold" /></button>
            </div>
            <input
              className="w-full border border-stone-300 rounded px-2 py-1.5 text-sm"
              placeholder="Título"
              value={editingTask.title || ''}
              onChange={e => setEditingTask(t => ({ ...t, title: e.target.value }))}
              data-testid="reading-edit-title"
            />
            <textarea
              className="w-full border border-stone-300 rounded px-2 py-1.5 text-sm"
              rows={5}
              placeholder="Texto (≥30 caracteres)"
              value={editingTask.text || ''}
              onChange={e => setEditingTask(t => ({ ...t, text: e.target.value }))}
              data-testid="reading-edit-text"
            />
            <div className="flex gap-2 items-center">
              <label className="text-xs font-semibold text-[#57534E]">Nivel:</label>
              <select
                value={editingTask.level || 'A2'}
                onChange={e => setEditingTask(t => ({ ...t, level: e.target.value }))}
                className="text-sm border border-stone-300 rounded px-2 py-1"
                data-testid="reading-edit-level"
              >
                {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            {(editingTask.questions || []).map((q, i) => (
              <div key={i} className="bg-[#FAF9F6] border border-stone-200 rounded p-3 space-y-2">
                <div className="flex gap-2 items-center">
                  <span className="text-xs bg-stone-200 px-2 py-0.5 rounded">{q.type === 'mc' ? 'Opción múltiple' : 'Respuesta corta'}</span>
                  <button
                    onClick={() => setEditingTask(t => ({ ...t, questions: t.questions.filter((_, k) => k !== i) }))}
                    className="ml-auto text-red-600 text-xs"
                  >
                    <Trash size={14} />
                  </button>
                </div>
                <input
                  className="w-full border border-stone-300 rounded px-2 py-1 text-sm"
                  placeholder="Enunciado"
                  value={q.prompt || ''}
                  onChange={e => setEditingTask(t => {
                    const qs = [...t.questions]; qs[i] = { ...qs[i], prompt: e.target.value }; return { ...t, questions: qs };
                  })}
                />
                {q.type === 'mc' ? (
                  <div className="space-y-1">
                    {(q.options || []).map((opt, oi) => (
                      <div key={oi} className="flex gap-2 items-center">
                        <input type="radio" name={`edit-corr-${i}`} checked={q.correct_index === oi}
                          onChange={() => setEditingTask(t => {
                            const qs = [...t.questions]; qs[i] = { ...qs[i], correct_index: oi }; return { ...t, questions: qs };
                          })} />
                        <input className="flex-1 border border-stone-300 rounded px-2 py-1 text-sm"
                          value={opt}
                          onChange={e => setEditingTask(t => {
                            const qs = [...t.questions]; const opts = [...qs[i].options]; opts[oi] = e.target.value; qs[i] = { ...qs[i], options: opts }; return { ...t, questions: qs };
                          })}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <ReadingKeywordsField
                    keywords={q.expected_keywords || []}
                    onChange={(next) => setEditingTask(t => {
                      const qs = [...t.questions]; qs[i] = { ...qs[i], expected_keywords: next }; return { ...t, questions: qs };
                    })}
                    testIdPrefix={`reading-edit-kw-${i}`}
                  />
                )}
              </div>
            ))}
            <div className="flex gap-2 pt-2 border-t border-stone-200">
              <button onClick={() => setEditingTask(null)} className="flex-1 bg-stone-200 px-3 py-2 rounded font-semibold" data-testid="reading-edit-cancel">Cancelar</button>
              <button onClick={saveEditingTask} className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C] text-white px-3 py-2 rounded font-semibold" data-testid="reading-edit-save">
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend modal — same UX as recordings/materials. */}
      {resendState && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResendState(null)} data-testid="reading-resend-dialog">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-bold text-lg text-[#292524]">An weitere Klassen zuweisen</h3>
                <p className="text-sm text-[#78716C] mt-1 truncate">{'"'}{resendState.task.title || '(sin título)'}{'"'}</p>
              </div>
              <button onClick={() => setResendState(null)} className="p-1 rounded-lg hover:bg-stone-100"><X size={18} /></button>
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => toggleResendTarget('__ALL__')}
                className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${resendState.selected.has('__ALL__') ? 'border-[#AB1C24] bg-[#AB1C24]/5' : 'border-stone-200 hover:border-stone-400'}`}
                data-testid="reading-resend-target-__ALL__"
              >
                <span className="font-semibold text-[#292524]">Alle Klassen</span>
                <span className="ml-2 text-xs text-[#78716C]">— Para todos los alumnos</span>
              </button>
              {groups.map(g => (
                <button
                  key={g.group_id}
                  type="button"
                  onClick={() => toggleResendTarget(g.group_id)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${resendState.selected.has(g.group_id) ? 'border-[#AB1C24] bg-[#AB1C24]/5' : 'border-stone-200 hover:border-stone-400'}`}
                  data-testid={`reading-resend-target-${g.group_id}`}
                >
                  <span className="font-semibold text-[#292524]">{g.name}</span>
                  {g.level && <span className="ml-2 text-xs text-[#78716C]">· {g.level}</span>}
                </button>
              ))}
              {groups.length === 0 && (
                <p className="text-xs text-[#A8A29E] italic px-1">Aún no hay clases definidas.</p>
              )}
            </div>
            <div className="flex gap-2 pt-2 border-t border-stone-200">
              <button onClick={() => setResendState(null)} className="flex-1 bg-stone-200 px-3 py-2 rounded font-semibold" data-testid="reading-resend-cancel">Cancelar</button>
              <button
                onClick={submitResend}
                disabled={resendState.selected.size === 0}
                className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C] text-white px-3 py-2 rounded font-semibold disabled:opacity-50"
                data-testid="reading-resend-submit"
              >
                Enviar a {resendState.selected.size || 0}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
